'use client';

import { Locale } from '@core/Entities/Locale/Locale.entity';
import { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { Status as VehicleStatus } from '@core/Entities/Vehicle/VehicleLiterals.entity';
import { getPromotionFlags } from '@core/Utils/Promotions/getPromotionFlags';
import { canShowDiscount } from '@core/Utils/Vehicle/CanShowDiscount';
import { isHeycarConnectDealer } from '@core/Utils/Vehicle/IsHeycarConnect';
import { isD2cModelVsp } from '@core/Utils/Vehicle/isD2cModelVsp';
import { BadgeItem } from '@gds/Badge/BadgeItem';

import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { useAtomValue } from 'jotai';

import styles from './PdpBadges.module.css';

interface Props {
  strings: Record<string, string>;
  vehicle: Vehicle;
  isLeasing?: boolean;
  locale: Locale;
}

const PdpBadges = ({ strings, vehicle, isLeasing, locale }: Props) => {
  const flags = useAtomValue(flagsAtom);
  const promotionFlags = getPromotionFlags(vehicle?.offers, flags);
  const isGenericEcomFlag = flags.genericEcomBadge;
  const isGenericInvestorFlag = flags.genericInvestorBadge;
  const isInvestorCampaignFlag = flags.investorCampaign;
  const isEcomCampaignFlag = flags.ecomCampaign;
  const isPercentageDiscountFlag = flags.percentageDiscount;
  const isFreeDeliveryFlag = flags.freeDelivery;
  const isComprehensivelyTestedFlag = flags.comprehensivelyTested;
  const hasHeyCarConnectBadge = flags.heycarConnect && isHeycarConnectDealer(vehicle);
  const isD2C = isD2cModelVsp(vehicle);
  const isEcom = vehicle.ecom.isEcom;
  const statusString = getStatusString(strings, vehicle?.details.status);
  const hasDiscount = canShowDiscount(vehicle.pricing);
  const reducedPricePercentage = vehicle.pricing?.reducedPricePercentage;

  return (
    <aside className={styles.badges}>
      <BadgeItem
        shallShowBadge={!!statusString}
        color="blue__dark"
        dataTestId="vehicle-tile-badge-status"
        label={statusString ?? ''}
      />
      {(isD2C || isEcom) && (
        <>
          <BadgeItem
            shallShowBadge={isD2C ? isGenericInvestorFlag : isGenericEcomFlag}
            color={isD2C ? 'mint_light' : 'blue'}
            dataTestId={isD2C ? 'vehicle-tile-badge-investor' : 'vehicle-tile-badge-ecom'}
            label={isD2C ? strings.investorGeneric : strings.ecomGeneric}
          />
          <BadgeItem
            shallShowBadge={isD2C ? isInvestorCampaignFlag : isEcomCampaignFlag}
            color="orange"
            dataTestId={
              isD2C
                ? 'vehicle-tile-badge-investor-ecom-campaign'
                : 'vehicle-tile-badge-ecom-campaign'
            }
            label={isD2C ? strings.investorPromo : strings.ecomPromo}
          />
        </>
      )}
      <BadgeItem
        shallShowBadge={isLeasing}
        color="orange"
        dataTestId="vehicle-tile-badge-leasing-offer"
        label={strings.leasingOffer}
      />
      <BadgeItem
        shallShowBadge={hasHeyCarConnectBadge}
        color="neutral__light"
        dataTestId="vehicle-tile-badge-connect-offer"
        label={strings.connect}
      />
      <BadgeItem
        shallShowBadge={promotionFlags?.isHeycarDaysCampaign && locale === 'fr'}
        color="mint"
        dataTestId="vehicle-tile-badge-leasing-offer"
        label={strings.heycarDaysPromotionLabel}
      />
      <BadgeItem
        shallShowBadge={isPercentageDiscountFlag && !!hasDiscount && !!reducedPricePercentage}
        color="error"
        dataTestId="vehicle-tile-badge-discount"
        label={`${Math.round(reducedPricePercentage ?? 0)}%`}
      />
      <BadgeItem
        shallShowBadge={isEcom && !hasDiscount && isFreeDeliveryFlag}
        color="orange"
        dataTestId="vehicle-tile-badge-free-delivery"
        label={strings.freeDelivery}
      />
      <BadgeItem
        shallShowBadge={!isEcom && isComprehensivelyTestedFlag}
        color="neutral__light"
        dataTestId="vehicle-tile-badge-comprehensively-tested"
        label={strings.comprehensivelyTested}
      />
    </aside>
  );
};

function getStatusString(
  strings: Record<string, string>,
  vehicleStatus: VehicleStatus | undefined,
) {
  switch (vehicleStatus) {
    case 'inactive':
      return strings.soldChipLabel;
    case 'reserved':
      return strings.reservedChipLabel;
    default:
      return null;
  }
}

export default PdpBadges;
